import { AxiosResponse } from 'axios'
import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import FailedImportRowDao from '@/interfaces/order/FailedImportRow.dao'
import FailedImportDetailDao from '@/interfaces/order/FailedImportDetail.dao'
import {
    ReleaseFailedImportResultDao,
    UpdateFailedImportAddressesDto,
    UpdateFailedImportGeneralDetailsDto,
    UpdateFailedImportLineDto,
} from '@/interfaces/order/FailedImportWrite.dto'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'

export default class FailedImportsService extends ApiService {
    async getFailedImports(params?: IRequestParameters): Promise<[FailedImportRowDao[], PageNumberPagination] | null> {
        return this.request<undefined, [FailedImportRowDao[], PageNumberPagination] | null>(
            'GET',
            '/failed-imports',
            params,
            undefined,
            undefined,
            'getFailedImports',
        ).catch(() => null)
    }

    async getFailedImport(id: number): Promise<FailedImportDetailDao | null> {
        return this.request<undefined, FailedImportDetailDao>(
            'GET',
            `/failed-imports/${id}`,
            undefined,
            undefined,
            undefined,
            'getFailedImport',
        ).catch(() => null)
    }

    async exportFailedImports(params: IRequestParameters): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'GET',
            '/failed-imports/export',
            params,
            undefined,
            'blob',
            'exportFailedImports',
        ).catch(() => null)
    }

    async getFailedImportCourierMappings(sellerId: string): Promise<string[] | null> {
        return this.request<undefined, string[]>(
            'GET',
            '/failed-imports/lookups/courier-mappings',
            { sellerId },
            undefined,
            undefined,
            'getFailedImportCourierMappings',
        ).catch(() => null)
    }

    async getFailedImportPaymentTypes(sellerId: string): Promise<Array<string | null> | null> {
        return this.request<undefined, Array<string | null>>(
            'GET',
            '/failed-imports/lookups/payment-types',
            { sellerId },
            undefined,
            undefined,
            'getFailedImportPaymentTypes',
        ).catch(() => null)
    }

    async updateFailedImportGeneralDetails(
        id: number,
        dto: UpdateFailedImportGeneralDetailsDto,
    ): Promise<FailedImportDetailDao | null> {
        return this.request<UpdateFailedImportGeneralDetailsDto, FailedImportDetailDao>(
            'PUT',
            `/failed-imports/${id}/general-details`,
            undefined,
            dto,
            undefined,
            'updateFailedImportGeneralDetails',
        ).catch(() => null)
    }

    async updateFailedImportAddresses(
        id: number,
        dto: UpdateFailedImportAddressesDto,
    ): Promise<FailedImportDetailDao | null> {
        return this.request<UpdateFailedImportAddressesDto, FailedImportDetailDao>(
            'PUT',
            `/failed-imports/${id}/addresses`,
            undefined,
            dto,
            undefined,
            'updateFailedImportAddresses',
        ).catch(() => null)
    }

    async updateFailedImportLine(
        id: number,
        lineId: number,
        dto: UpdateFailedImportLineDto,
    ): Promise<FailedImportDetailDao | null> {
        return this.request<UpdateFailedImportLineDto, FailedImportDetailDao>(
            'PUT',
            `/failed-imports/${id}/lines/${lineId}`,
            undefined,
            dto,
            undefined,
            'updateFailedImportLine',
        ).catch(() => null)
    }

    async releaseFailedImport(id: number): Promise<FailedImportDetailDao | null> {
        return this.request<undefined, FailedImportDetailDao>(
            'POST',
            `/failed-imports/${id}/release`,
            undefined,
            undefined,
            undefined,
            'releaseFailedImport',
        ).catch(() => null)
    }

    async releaseFailedImportToReview(id: number): Promise<FailedImportDetailDao | null> {
        return this.request<undefined, FailedImportDetailDao>(
            'POST',
            `/failed-imports/${id}/release-to-review`,
            undefined,
            undefined,
            undefined,
            'releaseFailedImportToReview',
        ).catch(() => null)
    }

    async archiveFailedImport(id: number): Promise<FailedImportDetailDao | null> {
        return this.request<undefined, FailedImportDetailDao>(
            'POST',
            `/failed-imports/${id}/archive`,
            undefined,
            undefined,
            undefined,
            'archiveFailedImport',
        ).catch(() => null)
    }

    async bulkReleaseFailedImports(ids: number[]): Promise<ReleaseFailedImportResultDao[] | null> {
        return this.request<{ ids: number[] }, ReleaseFailedImportResultDao[]>(
            'POST',
            '/failed-imports/bulk-release',
            undefined,
            { ids },
            undefined,
            'bulkReleaseFailedImports',
        ).catch(() => null)
    }
}
